<template>
  <div class="ov-page">
    <div class="ov-header">
      <div class="ov-note ov-note--info ov-header__note">
        <v-icon small>mdi-information-outline</v-icon>
        <span>{{ $t('city.subtitle') }} {{ $t('city.adviceText') }}</span>
      </div>
    </div>

    <div class="ov-card">
      <div class="ov-card__head">
        <div class="ov-card__title">
          Villes
          <span class="ov-count">{{ villesList.length }}</span>
        </div>
        <div class="ov-filters">
          <div class="ov-search">
            <v-icon small>mdi-magnify</v-icon>
            <input v-model="search" type="text" class="ov-input" :placeholder="$t('city.searchCity')" />
          </div>
          <button type="button" class="ov-btn ov-btn--primary ov-btn--sm" @click="openCreate">
            <v-icon small>mdi-plus</v-icon>
            {{ $t('city.addCity') }}
          </button>
        </div>
      </div>

      <v-data-table
        :headers="tableHeaders"
        :items="villesList"
        :search="search"
        :loading="loadingList"
        loader-height="2"
        loading-text="Chargement des villes..."
        no-data-text="Aucune ville enregistrée"
        no-results-text="Aucune ville ne correspond à la recherche"
        class="ov-table"
      >
        <template v-slot:[`item.designation`]="{ item }">
          <span class="cv-name">{{ item.designation }}</span>
        </template>
        <template v-slot:[`item.paysDesignation`]="{ item }">
          <span v-if="item.paysDesignation || item.pays || countryName(item.paysId)" class="ov-badge ov-badge--navy ov-badge--plain">
            {{ item.paysDesignation || item.pays || countryName(item.paysId) }}
          </span>
          <span v-else class="cv-muted">—</span>
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <div class="ov-row-actions cv-actions">
            <button type="button" class="ov-icon-btn" title="Modifier" @click="editVille(item)">
              <v-icon small>mdi-pencil-outline</v-icon>
            </button>
            <button type="button" class="ov-icon-btn ov-icon-btn--danger" title="Supprimer" @click="confirmDelete(item)">
              <v-icon small>mdi-trash-can-outline</v-icon>
            </button>
          </div>
        </template>
      </v-data-table>
    </div>

    <!-- ===== PANNEAU AJOUT / MODIFICATION ===== -->
    <v-navigation-drawer v-model="drawerOpen" right temporary fixed width="420" class="ov-panel">
      <div class="ov-panel__head">
        <div>
          <div class="ov-card__kicker">{{ isEditing ? 'Modification' : 'Nouvelle ville' }}</div>
          <div class="ov-card__title">{{ isEditing ? ville.designation || 'Modifier la ville' : $t('city.addCity') }}</div>
        </div>
        <button type="button" class="ov-icon-btn" title="Fermer" @click="closeDrawer">
          <v-icon small>mdi-close</v-icon>
        </button>
      </div>

      <form class="ov-panel__body" novalidate @submit.prevent="submitForm">
        <div class="ov-field">
          <label class="ov-label" for="cv-designation">{{ $t('city.designation') }}<span class="ov-required">*</span></label>
          <input
            id="cv-designation"
            v-model.trim="$v.ville.designation.$model"
            type="text"
            class="ov-input"
            :class="{ 'is-invalid': designationVilleErrors.length }"
            :placeholder="$t('city.designationPlaceholder')"
          />
          <div v-if="designationVilleErrors.length" class="ov-error">{{ designationVilleErrors[0] }}</div>
        </div>

        <div class="ov-field">
          <label class="ov-label" for="cv-pays">{{ $t('city.countryLabel') }}</label>
          <select id="cv-pays" v-model="ville.paysId" class="ov-select">
            <option :value="null">Aucun pays</option>
            <option v-for="pays in paysList" :key="pays.id" :value="pays.id">{{ pays.designation }}</option>
          </select>
          <div class="ov-hint">Le pays permet de filtrer et d'organiser les destinations.</div>
        </div>

        <button type="submit" class="cv-hidden-submit" tabindex="-1" aria-hidden="true"></button>
      </form>

      <template #append>
        <div class="ov-panel__foot">
          <button type="button" class="ov-btn ov-btn--secondary" :disabled="overlay" @click="isEditing ? closeDrawer() : reinitForm()">
            {{ isEditing ? 'Annuler' : $t('city.reset') }}
          </button>
          <button type="button" class="ov-btn ov-btn--primary" :disabled="overlay" @click="submitForm">
            <v-progress-circular v-if="overlay" indeterminate size="16" width="2"></v-progress-circular>
            <v-icon v-else small>mdi-content-save-outline</v-icon>
            {{ $t('city.save') }}
          </button>
        </div>
      </template>
    </v-navigation-drawer>

    <!-- ===== CONFIRMATION DE SUPPRESSION ===== -->
    <v-dialog v-model="deleteDialog" max-width="420" persistent>
      <div class="ov-dialog">
        <div class="ov-dialog__head">
          <div class="ov-dialog__title">Supprimer cette ville ?</div>
        </div>
        <div class="ov-dialog__body">
          <div class="ov-note ov-note--warning">
            <v-icon small color="#92400e">mdi-alert-outline</v-icon>
            <span>La ville <strong>{{ villeToDelete && villeToDelete.designation }}</strong> sera supprimée définitivement.</span>
          </div>
        </div>
        <div class="ov-dialog__foot">
          <button type="button" class="ov-btn ov-btn--secondary" :disabled="overlay" @click="deleteDialog = false; villeToDelete = null">Annuler</button>
          <button type="button" class="ov-btn ov-btn--danger" :disabled="overlay" @click="supprimerVille">
            <v-icon small>mdi-trash-can-outline</v-icon>
            Supprimer
          </button>
        </div>
      </div>
    </v-dialog>

    <v-alert class="myalert alert-success" type="success" width="350px" dismissible>{{ successMsg }}</v-alert>
    <v-alert class="myalert alert-warning" type="warning" width="350px" dismissible>{{ warningMsg }}</v-alert>
    <v-alert class="myalert alert-error" type="error" width="350px" dismissible>{{ errorMsg }}</v-alert>
  </div>
</template>

<script>
import axios from "axios";
import apiService from "@/services/api.service";
import { required } from "vuelidate/lib/validators";
import {
  API_CREER_VILLE,
  API_OBTENIR_LISTE_DES_VILLES_DISPONIBLE,
  HEADERS,
} from "../globalConfig/globalConstConfig";
import { showErrorMessage, showSuccessMessage, showWarningMessage } from "@/modules/messages/messageProcess";
export default {
  name: "CreerVille",
  data() {
    return {
      overlay: false,
      errorMsg: null,
      warningMsg: null,
      successMsg: null,
      drawerOpen: false,
      search: "",
      loadingList: false,
      editedVille: null,
      deleteDialog: false,
      villeToDelete: null,

      paysList: [],
      villesList: [],

      objectContainList: {
        datas: [],
      },

      ville: {
        id: null,
        designation: null,
        paysId: null,
      },

      tableHeaders: [
        { text: this.$t('city.city'), value: 'designation', sortable: true },
        { text: this.$t('city.countryLabel'), value: 'paysDesignation', sortable: true },
        { text: '', value: 'actions', sortable: false, align: 'end', width: '100px' },
      ],
    };
  },

  validations: {
    ville: {
      designation: {
        required,
      },
    },
  },

  
  methods: {
    reinitForm() {
      this.$v.$reset();
      this.ville = { id: null, designation: null, paysId: null };
      this.editedVille = null;
      this.objectContainList.datas = [];
    },

    openCreate() {
      this.reinitForm();
      this.drawerOpen = true;
    },

    closeDrawer() {
      this.drawerOpen = false;
    },

    // RECUPERER LA LISTE DES VILLES
    async obtenirListeVilles() {
      this.loadingList = true;
      await axios
        .post(API_OBTENIR_LISTE_DES_VILLES_DISPONIBLE, {}, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.status == 200 && response.data.items) {
            this.villesList = response.data.items;
          }
        })
        .catch((e) => {
          this.errorMsg = (e && e.message) || "Impossible de charger les villes.";
          showErrorMessage();
        })
        .finally(() => { this.loadingList = false; });
    },

    // RECUPERER LA LISTE DES PAYS DISPONIBLES (avec cache)
    async obtenirListePaysDisponible() {
      try {
        const response = await apiService.postForGet('pays/getAllPays', {});
        if (response.status.code == 800) {
          this.paysList = response.items;
        } else {
          this.errorMsg = response.status.message;
          showErrorMessage();
        }
      } catch (e) {
        this.errorMsg = e.message || this.$t('city.serverError');
        showErrorMessage();
      }
    },

    // CREATION (POST) OU MODIFICATION (PUT, AVEC L'ID) D'UNE VILLE
    async creerVille() {
      this.overlay = true;
      const ville = Object.assign({}, this.ville);
      if (!ville.id) delete ville.id;
      this.objectContainList.datas = [ville];
      const request = this.isEditing ? axios.put : axios.post;
      await request(API_CREER_VILLE, this.objectContainList, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.status == 200) {
            if (response.data.status.code == 800) {
              this.successMsg = response.data.status.message;
              showSuccessMessage();
              this.closeDrawer();
              this.obtenirListeVilles();
            } else {
              this.errorMsg = response.data.status.message;
              showErrorMessage();
            }
          } else if (response.status == 204) {
            this.warningMsg = this.$t('city.createError');
            showWarningMessage();
          } else {
            this.errorMsg = this.$t('city.createImpossible');
            showErrorMessage();
          }
        })
        .catch((e) => {
          this.errorMsg = (e && e.message) || this.$t('city.createImpossible');
          showErrorMessage();
        })
        .finally(() => {
          this.overlay = false;
          this.objectContainList.datas = [];
        });
    },

    // EDITION D'UNE VILLE
    editVille(item) {
      this.$v.$reset();
      this.editedVille = item;
      this.ville = { id: item.id, designation: item.designation, paysId: item.paysId || null };
      this.drawerOpen = true;
    },

    // CONFIRMER SUPPRESSION
    confirmDelete(item) {
      this.villeToDelete = item;
      this.deleteDialog = true;
    },

    // SUPPRESSION D'UNE VILLE
    async supprimerVille() {
      if (!this.villeToDelete) return;
      this.overlay = true;
      await axios
        .delete(`${API_CREER_VILLE}/${this.villeToDelete.id}`, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          const status = response.data && response.data.status;
          if (response.status == 200 && (!status || status.code == 800)) {
            this.successMsg = (status && status.message) || "Ville supprimée.";
            showSuccessMessage();
            this.obtenirListeVilles();
          } else {
            this.errorMsg = (status && status.message) || "Suppression impossible.";
            showErrorMessage();
          }
        })
        .catch((e) => {
          this.errorMsg = (e && e.message) || "Suppression impossible.";
          showErrorMessage();
        })
        .finally(() => {
          this.overlay = false;
          this.deleteDialog = false;
          this.villeToDelete = null;
        });
    },

    // ENVOI DU FORMULAIRE
    submitForm() {
      if (this.overlay) return;
      this.$v.$touch();
      if (this.$v.ville.$invalid) {
        this.errorMsg = this.$t('city.missingInfo');
        showErrorMessage();
      } else {
        this.creerVille();
      }
    },
  },

  computed: {
    isEditing() {
      return !!this.ville.id;
    },

    countryName() {
      return (id) => {
        const pays = this.paysList.find((p) => p.id === id);
        return pays ? pays.designation : null;
      };
    },
    designationVilleErrors() {
      const errors = [];
      if (!this.$v.ville.designation.$dirty) return errors;
      !this.$v.ville.designation.required &&
        errors.push(this.$t('city.designationRequired'));
      return errors;
    },
  },

  watch: {
    // Fermer le panneau (clic à l'extérieur compris) réinitialise le formulaire
    drawerOpen(open) {
      if (!open) this.reinitForm();
    },
  },

  mounted() {
    this.obtenirListePaysDisponible();
    this.obtenirListeVilles();
  },
};
</script>

<style lang="scss" scoped>
@import "../../styles/admin/ui";

.cv-name {
  font-weight: 700;
  color: $ov-navy;
}

.cv-muted {
  color: $ov-subtle;
}

.cv-actions {
  justify-content: flex-end;
}

.cv-hidden-submit {
  display: none;
}
</style>
